/**
 * @file VetDisponibilidadConfig.js
 * @description Configuración de disponibilidad estilo Calendly
 *
 * Secciones:
 * 1. Días que atiendo (checkboxes)
 * 2. Horarios por turno (máx 3)
 * 3. Días no disponibles (excepciones)
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import "bootstrap/dist/css/bootstrap.min.css";

import {
  getDisponibilidadConfig,
  guardarDisponibilidadConfig
} from "../../services/api";

// Días de la semana
const DIAS_SEMANA = [
  { key: "lunes", label: "Lunes" },
  { key: "martes", label: "Martes" },
  { key: "miercoles", label: "Miércoles" },
  { key: "jueves", label: "Jueves" },
  { key: "viernes", label: "Viernes" },
  { key: "sabado", label: "Sábado" },
  { key: "domingo", label: "Domingo" }
];

// Opciones de hora (intervalos de 30 min)
const HORAS_OPTIONS = [];
for (let h = 6; h <= 22; h++) {
  HORAS_OPTIONS.push(`${h.toString().padStart(2, "0")}:00`);
  if (h < 22) {
    HORAS_OPTIONS.push(`${h.toString().padStart(2, "0")}:30`);
  }
}

export default function VetDisponibilidadConfig() {
  const [searchParams] = useSearchParams();
  const vetId = searchParams.get("vetId") || "";
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Estado del formulario
  const [diasActivos, setDiasActivos] = useState([]);
  const [turnos, setTurnos] = useState([{ inicio: "09:00", fin: "14:00" }]);
  const [excepciones, setExcepciones] = useState([]);
  const [nuevaExcepcion, setNuevaExcepcion] = useState("");

  // Cargar configuración existente
  useEffect(() => {
    async function loadConfig() {
      if (!vetId) {
        setLoading(false);
        return;
      }
      try {
        const config = await getDisponibilidadConfig(vetId);
        if (config) {
          setDiasActivos(config.diasActivos || []);
          setTurnos(config.turnos?.length > 0 ? config.turnos : [{ inicio: "09:00", fin: "14:00" }]);
          setExcepciones(config.excepciones || []);
        }
      } catch (err) {
        console.error("Error cargando configuración:", err);
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, [vetId]);

  // Manejar cambio de día
  const handleDiaChange = (diaKey) => {
    setDiasActivos(prev => {
      if (prev.includes(diaKey)) {
        return prev.filter(d => d !== diaKey);
      } else {
        return [...prev, diaKey];
      }
    });
  };

  // Seleccionar todos los días laborales
  const handleSelectLaborales = () => {
    setDiasActivos(["lunes", "martes", "miercoles", "jueves", "viernes"]);
  };

  // Limpiar todos
  const handleClearDias = () => {
    setDiasActivos([]);
  };

  // Manejar cambio de turno
  const handleTurnoChange = (index, field, value) => {
    setTurnos(prev => {
      const newTurnos = [...prev];
      newTurnos[index] = { ...newTurnos[index], [field]: value };
      return newTurnos;
    });
  };

  // Agregar turno
  const handleAddTurno = () => {
    if (turnos.length >= 3) {
      toast.error("Máximo 3 turnos permitidos");
      return;
    }
    setTurnos(prev => [...prev, { inicio: "16:00", fin: "19:00" }]);
  };

  // Eliminar turno
  const handleRemoveTurno = (index) => {
    if (turnos.length <= 1) {
      toast.error("Debe haber al menos un turno");
      return;
    }
    setTurnos(prev => prev.filter((_, i) => i !== index));
  };

  // Agregar excepción
  const handleAddExcepcion = () => {
    if (!nuevaExcepcion) {
      toast.error("Selecciona una fecha");
      return;
    }
    if (excepciones.includes(nuevaExcepcion)) {
      toast.error("Esta fecha ya está agregada");
      return;
    }
    setExcepciones(prev => [...prev, nuevaExcepcion].sort());
    setNuevaExcepcion("");
  };

  // Eliminar excepción
  const handleRemoveExcepcion = (fecha) => {
    setExcepciones(prev => prev.filter(f => f !== fecha));
  };

  // Guardar configuración
  const handleSave = async () => {
    if (diasActivos.length === 0) {
      toast.error("Selecciona al menos un día de atención");
      return;
    }

    // Validar turnos
    for (const turno of turnos) {
      if (turno.inicio >= turno.fin) {
        toast.error("La hora de inicio debe ser menor que la hora de fin");
        return;
      }
    }

    setSaving(true);
    try {
      await guardarDisponibilidadConfig(vetId, {
        diasActivos,
        turnos,
        excepciones
      });
      toast.success("Configuración guardada correctamente");
    } catch (err) {
      console.error("Error guardando:", err);
      toast.error("Error al guardar: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/vetPanel?token=${token}`);
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  // LemmonTags brand colors
  const COLORS = {
    primary: "#84cc16",
    primaryDark: "#65a30d",
    primaryLight: "#ecfccb"
  };

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.primary }} className="text-white py-4">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="bi bi-calendar-check me-2"></i>
                Configurar Disponibilidad
              </h4>
              <small className="opacity-75">Define tu horario de atención semanal</small>
            </div>
            <button
              className="btn"
              style={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }}
              onClick={handleBack}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Volver
            </button>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {/* Sección 1: Días que atiendo */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white border-bottom">
            <h5 className="mb-0">
              <i className="bi bi-calendar-week me-2" style={{ color: COLORS.primary }}></i>
              Días que atiendo
            </h5>
          </div>
          <div className="card-body">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {DIAS_SEMANA.map(dia => (
                <button
                  key={dia.key}
                  type="button"
                  className="btn"
                  onClick={() => handleDiaChange(dia.key)}
                  style={{
                    minWidth: "100px",
                    backgroundColor: diasActivos.includes(dia.key) ? COLORS.primary : "transparent",
                    color: diasActivos.includes(dia.key) ? "white" : "#6c757d",
                    border: `1px solid ${diasActivos.includes(dia.key) ? COLORS.primary : "#6c757d"}`
                  }}
                >
                  {diasActivos.includes(dia.key) && (
                    <i className="bi bi-check-lg me-1"></i>
                  )}
                  {dia.label}
                </button>
              ))}
            </div>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm"
                onClick={handleSelectLaborales}
                style={{ color: COLORS.primary, borderColor: COLORS.primary, backgroundColor: "transparent" }}
              >
                Lunes a Viernes
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleClearDias}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Sección 2: Horarios por turno */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="bi bi-clock me-2" style={{ color: COLORS.primary }}></i>
              Horarios de atención
            </h5>
            {turnos.length < 3 && (
              <button
                type="button"
                className="btn btn-sm"
                style={{ color: COLORS.primary, borderColor: COLORS.primary, backgroundColor: "transparent" }}
                onClick={handleAddTurno}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Agregar turno
              </button>
            )}
          </div>
          <div className="card-body">
            {turnos.map((turno, index) => (
              <div key={index} className="d-flex align-items-center gap-3 mb-3 p-3 bg-light rounded">
                <span className="badge" style={{ backgroundColor: COLORS.primary }}>Turno {index + 1}</span>
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <select
                    className="form-select"
                    value={turno.inicio}
                    onChange={(e) => handleTurnoChange(index, "inicio", e.target.value)}
                    style={{ maxWidth: "120px" }}
                  >
                    {HORAS_OPTIONS.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                  <span className="text-muted">a</span>
                  <select
                    className="form-select"
                    value={turno.fin}
                    onChange={(e) => handleTurnoChange(index, "fin", e.target.value)}
                    style={{ maxWidth: "120px" }}
                  >
                    {HORAS_OPTIONS.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                {turnos.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleRemoveTurno(index)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            ))}
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              Puedes agregar hasta 3 turnos por día (ej: mañana, tarde, noche)
            </small>
          </div>
        </div>

        {/* Sección 3: Días no disponibles */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white border-bottom">
            <h5 className="mb-0">
              <i className="bi bi-calendar-x text-danger me-2"></i>
              Días no disponibles
            </h5>
          </div>
          <div className="card-body">
            <div className="d-flex gap-2 mb-3">
              <input
                type="date"
                className="form-control"
                value={nuevaExcepcion}
                onChange={(e) => setNuevaExcepcion(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                style={{ maxWidth: "200px" }}
              />
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleAddExcepcion}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Agregar
              </button>
            </div>

            {excepciones.length === 0 ? (
              <p className="text-muted mb-0">
                <i className="bi bi-check-circle me-1"></i>
                No hay excepciones configuradas
              </p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {excepciones.map(fecha => (
                  <span
                    key={fecha}
                    className="badge bg-danger d-flex align-items-center gap-2"
                    style={{ fontSize: "0.9rem", padding: "0.5rem 0.75rem" }}
                  >
                    {new Date(fecha + "T12:00:00").toLocaleDateString("es-MX", {
                      weekday: "short",
                      day: "numeric",
                      month: "short"
                    })}
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      style={{ fontSize: "0.6rem" }}
                      onClick={() => handleRemoveExcepcion(fecha)}
                    ></button>
                  </span>
                ))}
              </div>
            )}
            <small className="text-muted d-block mt-3">
              <i className="bi bi-info-circle me-1"></i>
              Agrega días festivos, vacaciones o fechas específicas donde no atenderás
            </small>
          </div>
        </div>

        {/* Botón Guardar */}
        <div className="d-grid gap-2">
          <button
            type="button"
            className="btn btn-lg"
            style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg me-2"></i>
                Guardar Disponibilidad
              </>
            )}
          </button>
        </div>

        {/* Resumen */}
        {diasActivos.length > 0 && (
          <div className="alert alert-info mt-4">
            <h6 className="alert-heading">
              <i className="bi bi-info-circle me-2"></i>
              Resumen de tu disponibilidad
            </h6>
            <p className="mb-1">
              <strong>Días:</strong> {diasActivos.map(d =>
                DIAS_SEMANA.find(ds => ds.key === d)?.label
              ).join(", ")}
            </p>
            <p className="mb-1">
              <strong>Horarios:</strong> {turnos.map((t, i) =>
                `${t.inicio} - ${t.fin}`
              ).join(" | ")}
            </p>
            {excepciones.length > 0 && (
              <p className="mb-0">
                <strong>Excepciones:</strong> {excepciones.length} día(s) bloqueado(s)
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
